import { Grid, Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import PageTemplate from '../../common/page-template/PageTemplate';
import { useCheckIfHasTemplate } from '../template/Template.hooks';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import { User } from '../../user/User.interface';
import { useFindCapaByTask } from './Capa.hook';
import { Capa, BaseCapaCardProps } from './Capa.interface';
import { CapaDetailsCard } from './components/CapaDetailsCard';
import CapaPreventiveCard from './components/CapaPreventiveCard';
import CapaVerificationCard from './components/CapaVerificationCard';
import CapaInvestigationCard from './components/CapaInvestigationCard';
import TaskStatusActions from '../tasks/TaskStatusActions';
import TaskStatusCard from '../tasks/cards/TaskStatusCard';
import { Task } from '../tasks/Task.interface';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import { useUserContext } from '../../user/User.context';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';
import { TemplateType } from '../template/Template.interface';
import CapaExportButton from './components/CapaExportButton';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';
import environment from '../../environments';

export const fullname = (user: User | undefined) =>
  user?.firstName + ' ' + user?.lastName;

const CapaDetailPage: React.FC = () => {
  const { hasRole } = useUserContext();
  const { taskId } = useParams<{ taskId?: string }>();
  const { data, error, get: getCapa } = useFindCapaByTask(Number(taskId));
  const [capa, setCapa] = useState<Capa>();
  const [templateAvailable, setTemplateAvailable] = useState<boolean>();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { isFieldDisabled } = useTaskEntityFields(capa?.task);

  const [{ data: ifHasTemplate }, get] = useCheckIfHasTemplate(
    TemplateType.Capa
  );

  const capaCardProps: BaseCapaCardProps = {
    capa: capa!,
    setErrorMessage,
    setSuccessMessage,
    onChange: (newCapa: Partial<Capa>) => {
      if (capa) {
        // notify task components to refresh
        capa.task = { ...capa.task };
        setCapa({ ...capa, ...newCapa });
      }
    }
  };

  const handleTaskChange = (newTask: Partial<Task>) => {
    if (!capa?.task) {
      return;
    }
    Object.assign(capa.task, newTask);
    capaCardProps.onChange?.({});
    setSuccessMessage('CAPA successfully updated.');
  };

  useEffect(() => {
    if (ifHasTemplate) {
      setTemplateAvailable(true);
    } else {
      setTemplateAvailable(false);
    }
  }, [ifHasTemplate]);

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    getCapa();
  }, [getCapa]);

  useEffect(() => {
    data && setCapa(data);
  }, [data]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>{capa && 'CAPA-' + capa.id}</h2>
        {capa?.task && (
          <PageTemplateHeaderActions flex>
            <TaskStatusActions
              task={capa.task}
              taskEntity={capa}
              onChange={(task: Task) => {
                capa.task = task;
                setCapa({ ...capa });
              }}
            />
            <Tooltip
              title={
                !templateAvailable
                  ? 'There are no templates configured for this item. Please contact your administrator'
                  : ''
              }
              placement='left'
              arrow
            >
              <div
                style={{
                  marginLeft: 10
                }}
              >
                <CapaExportButton
                  capa={capa}
                  buttonProps={{ disabled: !capa }}
                />
              </div>
            </Tooltip>
          </PageTemplateHeaderActions>
        )}
      </PageTemplateHeader>
      <PageTemplateDashboard>
        {capa && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Grid container spacing={3} direction='column'>
                <Grid item>
                  <CapaDetailsCard {...capaCardProps} />
                </Grid>
                <Grid item>
                  <CapaInvestigationCard {...capaCardProps} />
                </Grid>
                <Grid item>
                  <CapaPreventiveCard {...capaCardProps} />
                </Grid>
                <Grid item>
                  <CapaVerificationCard {...capaCardProps} />
                </Grid>
                {capa.task && (
                  <Grid item>
                    <TaskAttachmentCard
                      task={capa.task}
                      onError={(error: FetchError) => {
                        setErrorMessage(error.message);
                      }}
                      onChange={handleTaskChange}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={5}>
              <Grid container spacing={3} direction='column'>
                {capa.task && (
                  <>
                    <Grid item>
                      <TaskStatusCard task={capa.task} />
                    </Grid>
                    {capa.task?.children && capa.task.children.length > 0 && (
                      <Grid item>
                        <SubTaskListCard
                          title='Stakeholders'
                          task={capa.task}
                          disabled={isFieldDisabled('assignSubTasks')}
                          onChange={handleTaskChange}
                          onError={error => setErrorMessage(error.message)}
                        />
                      </Grid>
                    )}
                    <Grid item>
                      <TaskAssignCard task={capa.task} />
                    </Grid>
                    <Grid item>
                      <TaskFileListCard
                        task={capa.task}
                        onChange={handleTaskChange}
                        onError={error => setErrorMessage(error.message)}
                      />
                    </Grid>
                    <Grid item>
                      <TaskJiraIssueAssignCard
                        compact
                        task={capa.task}
                        entity={{
                          title: capa.title,
                          description: capa.description
                        }}
                        jiraFieldColumns={
                          environment.capaConfig?.visibleJiraFields
                        }
                        onChange={taskJiraIssues => {
                          // update capa Task jira issues on change
                          capa.task.jiraIssues = taskJiraIssues;
                          capaCardProps.onChange?.({ ...capa });
                        }}
                      />
                    </Grid>
                  </>
                )}
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title='CAPA'
                      apiPath={`capa`}
                      entity={capa}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <AppSnackbar severity='error' open={Boolean(errorMessage || error)}>
              {error?.message || 'There was an error saving.'}
            </AppSnackbar>
            <AppSnackbar
              severity='success'
              open={Boolean(successMessage)}
              onClose={() => setSuccessMessage('')}
            >
              {successMessage}
            </AppSnackbar>
          </Grid>
        )}
      </PageTemplateDashboard>
    </PageTemplate>
  );
};

export default CapaDetailPage;
